// tslint:disable:deprecation
import * as TableauUI from '@tableau/tableau-ui';
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { makeTestId } from '../../utils/Declarative';
import { findBlurRelatedTarget, selectInputText } from '../../utils/DomUtil';
import { IntegerStepperProps } from './IntegerStepperWidget';
import { LineTextFieldProps } from './LineTextFieldWidget';
import { OutlineTextFieldProps } from './OutlineTextFieldWidget';
import { SearchFieldProps } from './SearchFieldWidget';
import { TextFieldBaseProps, TextInputType } from './TextFieldBase';
import { TextFieldProps, TextFieldWidget } from './TextFieldWidget';

export { OutlineTextFieldProps, LineTextFieldProps, SearchFieldProps, IntegerStepperProps, TextInputType };

const ShimStyleName = TypeStyle.style({
  display: 'block',
  $nest: {
    '& input': { width: '100%' }
  }
});

const getLabelStyleName = (labelStyle: React.CSSProperties) => TypeStyle.style({
  $nest: {
    '& label': labelStyle
  }
});

const getMessageStyleName = (messageStyle: React.CSSProperties) => TypeStyle.style({
  $nest: {
    '& .message': messageStyle
  }
});

export interface LegacyTextFieldProps extends TextFieldProps, TextFieldBaseProps {
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onKeyUp?: React.KeyboardEventHandler<HTMLInputElement>;
  // these are just here for extra type-safety when doing the complicated prop composition below
  onBlur?: never;
  onChange?: never;
  onCompositionEnd?: never;
  onCompositionStart?: never;
  onFocus?: never;
}

function selectText(e: React.FocusEvent<HTMLInputElement>): void {
  selectInputText(e.target);
}

export class LegacyTextFieldShim extends React.Component<LegacyTextFieldProps> {
  public static defaultProps: Pick<LegacyTextFieldProps, 'shouldSelectTextOnFocus' | 'text' | 'type'> = Object.freeze({
    shouldSelectTextOnFocus: true,
    text: '',
    type: TextInputType.Text,
  });
  public static displayName: string = 'LegacyTextFieldShim';

  private isComposing: boolean = false;
  private enterDown: boolean = false;
  private escDown: boolean = false;

  private handleBlur: React.FocusEventHandler<HTMLInputElement> = e => {
    this.enterDown = false;
    this.escDown = false;
    e.relatedTarget = findBlurRelatedTarget(e)!; // polyfill for IE11
    this.props.handleBlur?.(e);
  }

  private handleChange: TextFieldProps['onChange'] = e => {
    if (this.props.disabled || !this.props.handleChange) { return; }
    this.isComposing = e.isComposing;
    this.props.handleChange((e.target as HTMLInputElement).value, this.isComposing);
  }

  private handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (this.props.disabled || this.isComposing) { return; }
    if (e.key === 'Enter' && this.props.handleEnter) {
      this.enterDown = true;
    } else if (e.key === 'Escape' && this.props.handleEscape) {
      this.escDown = true;
    } else if (this.props.onKeyDown) {
      this.props.onKeyDown(e);
    }
  }

  private handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (this.props.disabled || this.isComposing) { return; }
    if (e.key === 'Enter' && this.props.handleEnter && this.enterDown) {
      this.enterDown = false;
      this.props.handleEnter();
    } else if (e.key === 'Escape' && this.props.handleEscape && this.escDown) {
      this.escDown = false;
      this.props.handleEscape();
    } else if (this.props.onKeyUp) {
      this.props.onKeyUp(e);
    }
  }

  public render(): JSX.Element {
    const { className, containerStyle, handleBlur, handleChange, handleEnter, handleEscape, inputRef, inputStyle,
      onDarkBackground, placeholderStyle, shouldSelectTextOnFocus, text, type, ...textFieldProps } = this.props;

    if (placeholderStyle) {
      // intentionally ignored in the new world, native placeholder doesn't need this
    }

    return (
      <TextFieldWidget
        {...textFieldProps}
        className={TypeStyle.classes(
          className,
          ShimStyleName,
          TypeStyle.style(containerStyle),
          onDarkBackground && 'dark'
        )}
        onBlur={this.handleBlur}
        onChange={this.handleChange}
        onFocus={shouldSelectTextOnFocus ? selectText : undefined}
        onKeyDown={this.handleKeyDown}
        onKeyUp={this.handleKeyUp}
        ref={inputRef}
        style={inputStyle}
        type={type as string}
        value={text}
      />
    );
  }
}

/** @deprecated use `TextFieldWidget` with `kind='line'` */
export const LineTextFieldWidget: React.FC<LineTextFieldProps> = props => {
  const { errorText, hideErrorDiv, floatingLabel, floatingLabelStyle, ...textFieldBaseProps } = props;

  return (
    <LegacyTextFieldShim
      // this is not WCAG-compliant but it's what the old widget did
      {...(hideErrorDiv && { 'aria-errormessage': undefined, 'aria-describedby': undefined })}

      {...textFieldBaseProps}
      className={TypeStyle.classes(
        floatingLabelStyle && getLabelStyleName(floatingLabelStyle),
        hideErrorDiv && getMessageStyleName({ display: 'none' }),
      )}
      kind='line'
      message={hideErrorDiv ? undefined : (errorText || <br />)}
      label={floatingLabel}
      valid={(!hideErrorDiv && !!errorText) ? false : undefined}
    />
  );
};
// tslint:disable-next-line:deprecation
LineTextFieldWidget.displayName = 'LineTextFieldWidget';

/** @deprecated use `TextFieldWidget` with `kind='outline'` */
export const OutlineTextFieldWidget: React.FC<OutlineTextFieldProps> = props => {
  const { displayErrorTextSection, errorText, handleClearText, hasError, ...textFieldBaseProps } = props;

  return (
    <LegacyTextFieldShim
      // this is not WCAG-compliant but it's what the old widget did
      {...(!displayErrorTextSection && { 'aria-errormessage': undefined, 'aria-describedby': undefined })}

      {...textFieldBaseProps}
      className={TypeStyle.classes(
        !displayErrorTextSection && getMessageStyleName({ display: 'none' }),
      )}
      kind='outline'
      label={null}
      message={hasError ? errorText : <br />}
      onClear={handleClearText}
      valid={hasError ? false : undefined}
    />
  );
};
// tslint:disable-next-line:deprecation
OutlineTextFieldWidget.displayName = 'OutlineTextFieldWidget';

/** @deprecated use `TextFieldWidget` with `kind='search'` */
export const SearchFieldWidget: React.FC<SearchFieldProps> = props => {
  const { handleCancelSearch, ...textFieldBaseProps } = props;

  return (
    <LegacyTextFieldShim
      // this is not WCAG-compliant but it's what the old widget did
      aria-errormessage={undefined}
      aria-describedby={undefined}

      {...textFieldBaseProps}
      kind='search'
      label={null}
      onClear={handleCancelSearch}
    />
  );
};
// tslint:disable-next-line:deprecation
SearchFieldWidget.displayName = 'SearchFieldWidget';

export const IntegerStepperWidget: React.FC<IntegerStepperProps> = props => {
  // tslint:disable-next-line:max-line-length
  const { containerStyle, delta, handleBlur, inputStyle, labelStyle, onDarkBackground, onValueUpdated, pageDelta, shouldSelectTextOnFocus, start, testId, ...htmlProps } = props;
  const [value, setValue] = React.useState(start);

  const handleFocus: React.FocusEventHandler<HTMLInputElement> = e => {
    // TFS 1298800 -- reaching into the implementation details a bit here to prevent the selection highlight from toggling when using the stepper buttons
    if (shouldSelectTextOnFocus && !e.currentTarget.nextElementSibling?.contains(e.relatedTarget as HTMLElement)) {
      selectText(e);
    }
  };

  // TFS 969470 -- reset the value if `props.start` changes
  React.useEffect(() => setValue(props.start), [props.start]);

  return <TableauUI.Stepper
    {...htmlProps}
    className={TypeStyle.classes(
      TypeStyle.style(containerStyle),
      labelStyle && getLabelStyleName(labelStyle),
      onDarkBackground && 'dark'
    )}
    data-tb-test-id={makeTestId(testId, 'IntegerStepperWidget', 'TextInput')}
    floatingPoint={false}
    onBlur={handleBlur}
    onFocus={handleFocus}
    onValueChange={newValue => {
      setValue(newValue);
      onValueUpdated?.(newValue);
    }}
    pageSteps={pageDelta! / delta!}
    step={delta}
    style={inputStyle}
    value={value}
  />;
};
IntegerStepperWidget.defaultProps = { delta: 1, pageDelta: 1, shouldSelectTextOnFocus: true };
IntegerStepperWidget.displayName = 'IntegerStepperWidget';
